import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Lock,
  CreditCard,
  Bell,
  ShieldAlert,
  UserCheck,
  KeyRound,
  Network,
  Activity,
  Globe2,
  Headphones,
  Route as RouteIcon,
  Gauge,
  Clock,
  SignalHigh,
  BadgeCheck,
  Layers,
  Eye,
  Filter,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const whyBanksRely = [
  {
    title: "One-Time Passwords",
    description:
      "OTP SMS remains the most widely reachable way for a bank to confirm that the person signing in, approving a payment or resetting credentials is the account holder.",
    Icon: Lock,
    color: "text-amber",
  },
  {
    title: "Login Authentication",
    description:
      "Step-up authentication on web and mobile banking sessions relies on a code arriving while the customer is still on the login screen.",
    Icon: KeyRound,
    color: "text-indigo",
  },
  {
    title: "Transaction Confirmations",
    description:
      "A short confirmation that a card payment, transfer or standing order completed gives customers certainty and reduces calls to the contact centre.",
    Icon: CreditCard,
    color: "text-sky",
  },
  {
    title: "Payment Notifications",
    description:
      "Incoming payments, failed direct debits, settlement updates and low balance warnings are time-sensitive and are read fastest as a text message.",
    Icon: Bell,
    color: "text-violet",
  },
  {
    title: "Account Activity Alerts",
    description:
      "New device sign-ins, password changes, beneficiary additions and limit changes are communicated so unexpected activity is spotted quickly.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Fraud and Security Alerts",
    description:
      "Suspicious-activity warnings let a customer confirm or reject a transaction before losses grow, which depends on the message arriving promptly.",
    Icon: ShieldAlert,
    color: "text-rose",
  },
  {
    title: "Service Notifications",
    description:
      "Statement availability, card delivery, branch or service updates and scheduled maintenance notices keep customers informed without relying on push alone.",
    Icon: MessageSquare,
    color: "text-navy",
  },
  {
    title: "Customer Verification",
    description:
      "Onboarding, re-verification and contact detail changes are confirmed by checking that the customer controls the phone number on the account.",
    Icon: UserCheck,
    color: "text-pink",
  },
];

const challenges = [
  {
    title: "Delayed OTPs",
    description:
      "When a passcode arrives after the entry window has closed, the customer retries or abandons the session. Latency, not only delivery, decides whether authentication works.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Failed Messages",
    description:
      "Messages that never reach the handset create contact centre volume, blocked logins and payments that stall halfway through approval.",
    Icon: SignalHigh,
    color: "text-rose",
  },
  {
    title: "Inconsistent Delivery by Country",
    description:
      "A route that performs well in one market can behave differently in another, so banks serving customers across borders often see quality vary destination by destination.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Indirect or Low-Quality Routes",
    description:
      "Traffic passed through long chains of intermediaries is less predictable, harder to troubleshoot and more exposed to being delayed or altered along the way.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Filtering",
    description:
      "Operator and regulatory filtering can block or hold messages based on content, sender or traffic patterns, sometimes without a clear error being returned.",
    Icon: Filter,
    color: "text-violet",
  },
  {
    title: "Sender ID Issues",
    description:
      "Sender ID rules differ between countries. Where identifiers must be registered, pre-approved or are overwritten, brand recognition and deliverability can both suffer.",
    Icon: BadgeCheck,
    color: "text-lime",
  },
  {
    title: "Limited Delivery Visibility",
    description:
      "Without clear delivery reporting, teams cannot tell whether a problem sits in the banking application, the messaging provider or the destination network.",
    Icon: Eye,
    color: "text-navy",
  },
  {
    title: "Country-Specific Troubleshooting",
    description:
      "Investigating why one market underperforms takes operator-level knowledge and someone willing to look at the route rather than close the ticket.",
    Icon: Gauge,
    color: "text-pink",
  },
];

const howWeSupport = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 17 years of experience in voice and messaging. That background shapes how banking traffic is built and troubleshooted.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Routing Quality",
    description:
      "Authentication and transactional traffic is routed through connections chosen for reliability rather than lowest cost, with decisions made per destination and traffic type.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Global Connectivity",
    description:
      "International messaging is delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as customer bases expand.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Monitoring and Operational Attention",
    description:
      "Traffic is monitored so performance changes are noticed and acted on, and so your team has delivery insight to work from when investigating an issue.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Responsive Client Support",
    description:
      "A team built for enterprise clients works directly with your engineering and operations people instead of routing you through a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Practical Troubleshooting",
    description:
      "When a route looks wrong we investigate it at operator level and change it, rather than leaving traffic in place and reporting the same numbers back.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const useCases = [
  {
    title: "OTP & Login Authentication",
    description:
      "Deliver one-time passcodes for online and mobile banking sign-in, credential resets and device changes, where speed and delivery both decide whether the session completes.",
    Icon: Lock,
    color: "text-amber",
    href: "/use-cases/otp-verification",
    linkLabel: "OTP verification",
  },
  {
    title: "Transaction Notifications",
    description:
      "Send card usage, transfer and account activity alerts as they happen, over routes intended for time-critical transactional traffic.",
    Icon: Bell,
    color: "text-sky",
    href: "/use-cases/transactional-alerts",
    linkLabel: "Transactional alerts",
  },
  {
    title: "Payment Confirmations",
    description:
      "Confirm payments, transfers, refunds and settlements so customers and counterparties know where money is without opening the banking app.",
    Icon: CreditCard,
    color: "text-indigo",
    href: "/use-cases/payment-fintech",
    linkLabel: "Payment messaging",
  },
  {
    title: "Account Alerts",
    description:
      "Notify customers about balances, limits, statement availability and account changes so they stay informed between logins.",
    Icon: Layers,
    color: "text-lime",
    href: "/use-cases/platform-notifications",
    linkLabel: "Platform notifications",
  },
  {
    title: "Fraud & Security Alerts",
    description:
      "Warn customers about suspicious logins or unusual transactions and let them respond before further activity takes place.",
    Icon: ShieldAlert,
    color: "text-rose",
    href: "/use-cases/fraud-prevention",
    linkLabel: "Fraud prevention",
  },
  {
    title: "Identity Verification",
    description:
      "Support onboarding and account re-verification with messages that confirm a customer controls the phone number held on file.",
    Icon: UserCheck,
    color: "text-violet",
    href: "/use-cases/identity-verification",
    linkLabel: "Identity verification",
  },
  {
    title: "Customer Notifications",
    description:
      "Keep customers updated on service changes, appointments and requests for action, using a channel that does not depend on an installed app.",
    Icon: MessageSquare,
    color: "text-navy",
    href: "/solutions/a2p-sms",
    linkLabel: "A2P SMS",
  },
];

const deliveryFactors = [
  {
    title: "Delivery Rate",
    description:
      "For a marketing campaign an undelivered message is a lost impression. For a banking OTP it is a customer who cannot log in or approve a payment.",
    Icon: SignalHigh,
    color: "text-amber",
  },
  {
    title: "Latency",
    description:
      "Authentication codes are only useful inside a short window, so consistent delivery within seconds matters as much as the overall delivery percentage.",
    Icon: Clock,
    color: "text-sky",
  },
  {
    title: "Route Quality",
    description:
      "Direct, well-understood routes behave predictably. Long intermediary chains introduce variability that is difficult to diagnose after the fact.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Sender ID",
    description:
      "A recognisable, correctly registered sender helps customers trust a banking message and helps that message reach the handset where registration rules apply.",
    Icon: BadgeCheck,
    color: "text-violet",
  },
  {
    title: "Filtering",
    description:
      "Operator filtering can hold or drop traffic based on content and sending patterns, which is why message structure and route choice are handled together.",
    Icon: Filter,
    color: "text-pink",
  },
  {
    title: "Redundancy",
    description:
      "Alternative routing paths matter when a network or connection degrades, so authentication traffic does not depend on a single option.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Monitoring",
    description:
      "Delivery visibility turns a vague complaint about missing codes into a specific destination, operator and time window your team can act on.",
    Icon: Activity,
    color: "text-rose",
  },
  {
    title: "Delivery Visibility",
    description:
      "A message accepted by a platform is not the same as a message read by a customer. Reporting has to reflect what happened at the handset, not just at submission.",
    Icon: Eye,
    color: "text-navy",
  },
];

const faqs = [
  {
    question: "What is SMS used for in banking?",
    answer:
      "Banks use SMS mainly for authentication and transactional communication. That includes one-time passwords for online and mobile banking sign-in, transaction and payment confirmations, balance and account activity alerts, security notifications such as new device logins or password changes, fraud warnings, and verification messages during onboarding or when contact details change. These messages are time-sensitive and are expected by the customer, so they are normally sent as transactional A2P SMS rather than through marketing channels or in-app notifications alone, which depend on an app being installed and enabled.",
  },
  {
    question: "Why do banks use OTP SMS?",
    answer:
      "OTP SMS gives a bank a second factor that works on almost any mobile phone, without requiring an app, a data connection or a hardware token. The customer receives a short single-use code, enters it back into the banking session, and the bank confirms that the person acting on the account controls the registered phone number. Because the code expires within a short window, it is of limited use to an attacker who intercepts it later. Reach and familiarity are the main reasons SMS remains widely used for banking authentication.",
  },
  {
    question: "Why do banking SMS messages fail or arrive late?",
    answer:
      "Common causes include indirect routing to the destination network, operator or regulatory filtering, sender ID rules that are not met in a particular country, congestion on a shared route, latency that pushes a code past its expiry window, and numbers that are unreachable, ported or switched off. Delays also occur when traffic is passed through several intermediaries, each adding processing time. Without delivery reporting at operator level it is often unclear which factor applies, which is why route quality and monitoring matter as much as the message content itself.",
  },
  {
    question: "How can banks improve SMS delivery rates?",
    answer:
      "Send authentication and alert traffic over routes intended for transactional messages rather than the cheapest available option, and work with a provider that has direct operator connectivity in your main markets. Register sender IDs where local rules require it, keep authentication messages short and clearly worded, and avoid content patterns that attract filtering. Monitor delivery per destination and per operator rather than as a single global figure, make sure alternative routing is available if a path degrades, and investigate country-specific problems at route level instead of retrying blindly.",
  },
  {
    question: "What is transactional SMS for financial institutions?",
    answer:
      "Transactional SMS is triggered by a customer action or an account event rather than a marketing campaign. For a financial institution that means one-time passwords, transaction and payment confirmations, balance and limit alerts, security notifications and fraud warnings. Because the recipient is expecting the message and often waiting for it, transactional traffic is routed with speed and reliability as the priority. Regulators and mobile operators generally treat it differently from promotional traffic, which usually requires prior marketing consent and is not held to the same delivery expectations.",
  },
  {
    question: "How does SMS routing affect banking message delivery?",
    answer:
      "Routing determines the path a message takes between the bank's application and the customer's handset, and therefore how quickly and reliably it arrives. Direct connections to mobile network operators shorten that path, so there are fewer points where a message can be delayed, filtered or modified, and clearer information when something goes wrong. Longer chains of intermediaries are typically cheaper but less predictable and much harder to troubleshoot. For banking traffic, where a late code blocks a login, route quality is usually the deciding factor.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SMS Messaging for Banks and Financial Institutions",
  serviceType: "A2P SMS messaging",
  description:
    "Enterprise A2P SMS for banks and financial institutions, covering OTP authentication, transaction alerts, payment notifications, fraud and security alerts, and international delivery.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType: "Banks, financial institutions and financial services providers",
  },
  url: "https://yootelco.com/industries/banks-financial-institutions",
};

const IndustriesBanks = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS for Banks & Financial Institutions | A2P Messaging | Yootelco"
        description="Reliable SMS for banks and financial institutions, including OTP authentication, transaction alerts, payment notifications and security messaging. Talk to Yootelco."
        path="/industries/banks-financial-institutions"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Messaging for Banking
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              SMS Messaging for Banks &amp; Financial Institutions
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Banks and financial institutions depend on timely SMS for customer authentication, transaction notifications, security alerts and everyday communication. Yootelco delivers that traffic with a telecom-native approach: quality routing, delivery monitoring, operational visibility and support that engages with your team when a route needs attention.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "Banking OTP SMS", Icon: Lock },
                { label: "Transaction Alerts", Icon: Bell },
                { label: "Enterprise A2P SMS", Icon: SignalHigh },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Enterprise SMS for Banking Communication</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Retail and commercial banking has moved almost entirely to digital channels, yet the single most important message a bank sends is often still a text message. A customer signing in to online banking, approving a card payment, checking whether a transfer landed or responding to a suspected fraud attempt is waiting on an SMS at that exact moment. If it is late, the login fails. If it never arrives, the customer calls the contact centre or gives up.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            SMS holds that position because it does not depend on an app being installed, push notifications being enabled or a data connection being available. Authentication codes, transaction confirmations, account notifications, payment alerts, security messages, fraud warnings and general customer communication all reach the handset through the same channel, which is why banks and financial institutions continue to treat{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            as core infrastructure rather than a marketing tool.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The difference between a messaging channel that supports the bank and one that quietly costs it customers is delivery quality. That means how fast a message arrives, how consistently it arrives in every market served, and how quickly the reason for a failure can be identified when something changes on a route.
          </p>
        </div>
      </section>

      {/* Why SMS Matters */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Matters for Banks and Financial Institutions</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Banking SMS is rarely optional. Most of it is triggered by a customer action or an account event, and in almost every case the recipient is either waiting for the message or needs to act on it. These are the scenarios where delivery quality is felt most directly.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {whyBanksRely.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            What these have in common is timing. A code that arrives two minutes late is useless, and a fraud alert read the next morning arrives after the damage. For banking traffic, delivery and speed are the same requirement, which is why authentication and alert messaging is treated separately from promotional sending.
          </p>
        </div>
      </section>

      {/* Challenges */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Common SMS Challenges for Banks</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Most banking messaging problems are not caused by the message itself. They come from how traffic is routed, how each destination network treats it, and how much visibility the sending team has when conditions change. These are the issues banking engineering and operations teams raise most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {challenges.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            No provider removes every one of these factors, since operator behaviour and local regulation sit outside any single supplier's control. What can be improved is the path traffic takes, how closely it is watched, and how quickly a specific destination problem is identified and acted on.
          </p>
        </div>
      </section>

      {/* How Yootelco Supports */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Supports Banks and Financial Institutions</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco works on the messaging that has to function: verification, confirmation and security. Our approach is built around route quality, monitoring and hands-on support rather than volume for its own sake.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeSupport.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Where a financial institution also needs to reach customers by phone, for example to confirm a high-value transaction or handle an inbound service call, Yootelco supports{" "}
            <Link to="/solutions/voice-termination" className="font-semibold text-primary underline-offset-4 hover:underline">
              voice termination
            </Link>{" "}
            and{" "}
            <Link to="/solutions/two-factor-authentication" className="font-semibold text-primary underline-offset-4 hover:underline">
              two-factor authentication
            </Link>{" "}
            alongside messaging.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">SMS Use Cases for Banks</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The messaging requirements of a retail bank, a private bank and a payments institution overlap more than they differ. These are the use cases banking teams bring to us most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} className="mx-auto block w-fit" />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              <Link
                to={item.href}
                className="mt-4 text-[13px] font-bold text-primary underline-offset-4 hover:underline"
              >
                {item.linkLabel}
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Delivery Quality */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Delivery Quality Matters in Banking</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            A message being sent is not the same as a customer receiving it quickly. Traffic can be accepted by a platform, passed between intermediaries, held by a filter and eventually delivered long after the customer needed it, or dropped without a meaningful error. For banking messaging, that gap between submission and arrival is where delivery quality lives, and it is measured across several dimensions at once.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {deliveryFactors.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            This is where a telecom-native approach makes a practical difference. Coming from the carrier side means route decisions, operator relationships and delivery investigation are handled as network problems rather than support tickets. You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Global */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global SMS Messaging for Financial Institutions</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Financial institutions rarely serve one market only. Customers travel, hold accounts abroad, receive payments across borders, or belong to a group operating in several countries. Each destination brings its own operator environment, local filtering practices, sender ID expectations and traffic conditions, and messaging that performs well domestically does not automatically perform well elsewhere.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Direct carrier connectivity matters because it shortens the path between the banking platform and the customer's handset. Fewer intermediaries means fewer places for a message to be delayed, filtered or altered, and clearer answers when one destination starts behaving differently from the rest. Regional differences in latency and delivery are normal, so routing is configured per destination and per traffic type rather than applied uniformly.
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Routing configured per destination and traffic type",
              "Delivery monitoring across international routes",
              "Support for sender ID requirements where local rules apply",
              "Route changes made when a destination underperforms",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & CX */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Balancing Security, Speed and Customer Experience</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Security messaging only works when it arrives at the right time. An OTP that is delayed does not make a login safer, it simply makes it fail, and customers respond by requesting more codes, switching channels or contacting support. Every retry adds cost and erodes confidence in the digital channel the bank has invested in.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The same applies to activity messaging. Transaction alerts help customers recognise their own account behaviour and notice quickly when something is unfamiliar, which is only useful while the transaction is recent. Fraud alerts depend even more heavily on timing, because their purpose is to give the customer a chance to act before further activity takes place.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Consistency ties it together. When messages arrive reliably, in a recognisable format, from a sender the customer associates with their bank, the channel becomes something customers trust and act on rather than ignore. Reliable delivery is therefore both a security control and a customer experience decision.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Ready to Improve Your Banking SMS Delivery?"
        description="Tell us about your authentication, transaction alert and customer notification requirements and we'll show you how Yootelco can support them."
        useCase="Banks & Financial Institutions"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Conclusion */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Reliable Messaging for Banking Operations</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Banks and financial institutions depend on SMS for the moments that matter most to customers: proving who they are, confirming that money moved, and warning them when something looks wrong. Those messages carry real operational and security weight, so the channel behind them needs to behave predictably every day, in every market served.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            That predictability comes from delivery quality and routing rather than from volume or price alone. Yootelco's telecom-native approach, built on years of voice and messaging experience, direct operator connectivity, monitoring and responsive support, is designed for exactly this kind of enterprise messaging.
          </p>
          <Link
            to="/contact"
            className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesBanks;
